import { collection, getDocs, getFirestore } from 'firebase/firestore';

// 公開求人ページ用: 求人票の年度別サブコレクション
// recruits/{recruitId}/jobPostings/{卒業年度} からデータを読む。
//
// recruitId は既存のフラットドキュメントID（"{companyId}_2027"）で、その
// ドキュメント配下の jobPostings サブコレクションに、年度別・求人票専有の
// フィールド（info / target / employment20 / employment18 / employment17 /
// benefit / applicationInfo / hashTag / jobPostingsStatus）が入っている。
//
// パス規約（親 = フラットドキュメント {companyId}_2027、子 = 卒業年度）は
// サロン側の保存先ヘルパー src/salon/desktop/2.jobPostings/jobPostingRef.ts と
// 同一。src/pages 配下から src/salon の実装を import しない方針のため、
// 読み取りに必要な最小限だけをここに置いている。
//
// ロジックは memorii（学生アプリ）本体 lib/9.type/recruit/job_posting.dart の
// _hasEmploymentData / loadLatestJobPostingYearData / loadAllJobPostingYearData
// と同一の判定基準に揃えている。

const JOB_POSTING_SUBCOLLECTION = 'jobPostings';

export type JobPostingYearData = Record<string, any>;

// サロン側の求人票編集画面は、卒業年度タブを追加した時点で employment20 を
// 空プレースホルダー（空文字フィールドのMap）として作成する。そのため
// 「employment20 が存在する」だけでは「実際に入力済み」と判定できない。
// isCompleted はサロン側がその年度の雇用情報セクションの入力を完了した時に
// 立てるフラグで、これが true の年度だけを「実データが入っている」とみなす。
function hasEmploymentData(employment20: unknown): boolean {
  return (
    typeof employment20 === 'object' &&
    employment20 !== null &&
    (employment20 as Record<string, unknown>).isCompleted === true
  );
}

function sortYearIdsDescending(ids: string[]): string[] {
  return [...ids].sort((a, b) => (Number(b) || -1) - (Number(a) || -1));
}

// jobPostings 配下から「employment20 が入力済みの最新卒業年度」の
// ドキュメントデータを返す。ドキュメントID（卒業年度）を数値として降順に
// 走査し、最初に isCompleted な年度を採用する（該当が無ければ null）。
// 呼び出し側は null のとき従来どおりフラットドキュメント本体を使う。
export async function loadLatestJobPostingYearData(
  recruitId: string,
): Promise<JobPostingYearData | null> {
  if (!recruitId) return null;

  const db = getFirestore();

  let snap;
  try {
    snap = await getDocs(
      collection(db, 'recruits', recruitId, JOB_POSTING_SUBCOLLECTION),
    );
  } catch {
    return null;
  }

  const docsById = new Map<string, JobPostingYearData>();
  snap.forEach((docSnap) => {
    docsById.set(docSnap.id, docSnap.data());
  });

  const orderedYears = sortYearIdsDescending([...docsById.keys()]);
  for (const year of orderedYears) {
    const data = docsById.get(year);
    if (data && hasEmploymentData(data.employment20)) {
      return data;
    }
  }

  return null;
}

// jobPostings 配下のうち、実際に内容が入っている（employment20 が
// isCompleted の）卒業年度をすべて返す（キー = 年度文字列）。
// 求人詳細ページの「卒業年度タブ」用。該当が無ければ空オブジェクト
// （呼び出し側は従来どおり単年度表示にフォールバックする）。
export async function loadAllJobPostingYearData(
  recruitId: string,
): Promise<Record<string, JobPostingYearData>> {
  if (!recruitId) return {};

  const db = getFirestore();

  let snap;
  try {
    snap = await getDocs(
      collection(db, 'recruits', recruitId, JOB_POSTING_SUBCOLLECTION),
    );
  } catch {
    return {};
  }

  const out: Record<string, JobPostingYearData> = {};
  snap.forEach((docSnap) => {
    const data = docSnap.data();
    if (hasEmploymentData(data.employment20)) {
      out[docSnap.id] = data;
    }
  });

  return out;
}
